/**
 * StudyView — the annotator role's study view for a single worklist
 * (`/labeling-overview/:category/:projectId` when the resolved role is
 * "annotator"). Markup mirrors mockup `#s-astudy`
 * (PLANS/reviews/2026-07-07-labeling-overview-redesign-mockup.html): a
 * `WorklistDropdown` for switching between the annotator's batches under the
 * same factory project, a `CountStrip`, and a reporting-vibe case list (row
 * markup copied from `ReportingStudyHome.tsx`) that opens the viewer at the
 * clicked case.
 *
 * Reached from AnnotatorCategory's resume hero / batch rows, or from the
 * viewer's "Work List" button (`LabelingPage.handleWorkList`) — see the
 * mockup's callout on `#s-astudy`.
 *
 * # Data sources
 * `useWorklistQuery(worklistProjectId)` supplies the case list itself
 * (same shape `ReportingStudyHome` renders). `useCasesQuery` supplies real
 * per-case `completion_status` for the Done/Pending row badge and the
 * CountStrip's done/to-go counts — `Worklist.studies[].status` exists but
 * has no documented semantics anywhere else in the codebase, so deriving a
 * badge from it would be a guess. Per this repo's no-fabricated-data rule,
 * the done/to-go counts are omitted entirely (not rendered as 0/0) until
 * `useCasesQuery` resolves.
 *
 * The dropdown's batch list comes from `useMyEnrollmentsQuery` (batches for
 * the annotator's own projects). The worklist grouping logic mirrors
 * `AnnotatorCategory`'s `projectGroups`: find the enrollment owning the
 * routed `worklistProjectId`, then list every enrollment sharing that
 * enrollment's `project_id` (its sibling batches/phases).
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useMyEnrollmentsQuery } from "../../../hooks/useFactoryQueries";
import { useCasesQuery, useWorklistQuery } from "../../../hooks/useLabelingQueries";
import type { EnrollmentState } from "../../../types/factory";
import { batchSeq } from "../../../utils/factoryDisplay";
import type { LabelingCategoryKey } from "../../../utils/labelingCategories";
import { FaChevronRight, FaFileAlt } from "../../../utils/icons";
import { CountStrip, StatusBadge } from "./primitives";
import { WorklistDropdown, type WorklistDropdownItem } from "./WorklistDropdown";

const STATE_TAG: Record<EnrollmentState, string> = {
  active: "active",
  pending_approval: "submitted for review",
  blocked_pool_empty: "blocked (pool empty)",
  completed: "completed",
};

interface StudyViewProps {
  categoryKey: LabelingCategoryKey;
  worklistProjectId: string;
}

export function StudyView({ categoryKey, worklistProjectId }: StudyViewProps) {
  const navigate = useNavigate();
  const [selectedWorklistProjectId, setSelectedWorklistProjectId] = useState(worklistProjectId);

  const myEnrollmentsQuery = useMyEnrollmentsQuery();
  const worklistQuery = useWorklistQuery(selectedWorklistProjectId);
  const casesQuery = useCasesQuery(selectedWorklistProjectId);

  const dropdownItems: WorklistDropdownItem[] = useMemo(() => {
    const enrollments = myEnrollmentsQuery.data ?? [];
    const owning =
      enrollments.find((e) => e.worklist_project_id === selectedWorklistProjectId) ??
      enrollments.find((e) => e.worklist_project_id === worklistProjectId);
    const siblings = owning
      ? enrollments.filter((e) => e.project_id === owning.project_id)
      : enrollments.filter((e) => e.worklist_project_id === worklistProjectId);

    return siblings
      .filter((e) => !!e.worklist_project_id)
      .map((e) => ({
        id: e.worklist_project_id as string,
        label: e.current_batch_id ? `Batch ${batchSeq(e.current_batch_id)}` : "No batch",
        tag: e.annotation_read_only
          ? "Paused · view only"
          : `Phase ${e.phase} · ${STATE_TAG[e.state]}`,
        tone:
          !e.annotation_read_only && e.state === "active" ? ("sig" as const) : ("ghost" as const),
      }));
  }, [myEnrollmentsQuery.data, selectedWorklistProjectId, worklistProjectId]);

  const studies = useMemo(() => worklistQuery.data?.studies ?? [], [worklistQuery.data]);

  const caseStatusById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of casesQuery.data?.cases ?? []) map.set(c.case_id, c.completion_status);
    return map;
  }, [casesQuery.data]);

  const hasCompletionData = casesQuery.data !== undefined;
  const doneCount = useMemo(
    () => studies.filter((s) => caseStatusById.get(s.case_id) === "complete").length,
    [studies, caseStatusById],
  );

  const countItems = [`${studies.length} cases`];
  if (hasCompletionData) {
    countItems.push(`${doneCount} done`, `${studies.length - doneCount} to go`);
  }

  const handleOpen = (caseIndex: number) => {
    navigate(
      `/labeling?project=${encodeURIComponent(selectedWorklistProjectId)}&case=${caseIndex}`,
    );
  };

  return (
    <div className="h-full overflow-auto text-white page-premium">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <button
          type="button"
          onClick={() => navigate(`/labeling-overview/${categoryKey}`)}
          className="mb-4 text-xs text-fg-muted transition hover:text-fg"
        >
          ← Back
        </button>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-fg">{worklistQuery.data?.name ?? "Study view"}</h1>
        </div>

        <div className="mb-6 flex flex-wrap items-center justify-between gap-3.5">
          <WorklistDropdown
            worklists={dropdownItems}
            selectedId={selectedWorklistProjectId}
            onSelect={setSelectedWorklistProjectId}
          />
          <CountStrip items={countItems} />
        </div>

        {worklistQuery.isLoading ? (
          <div className="rounded border border-border/50 bg-surface p-8 text-center text-sm text-fg-muted">
            Loading worklist…
          </div>
        ) : worklistQuery.error || !worklistQuery.data ? (
          <div className="rounded border border-warn/30 bg-warn-tint p-8 text-center text-sm text-warn">
            Failed to load worklist
          </div>
        ) : studies.length === 0 ? (
          <div className="rounded border border-border/50 bg-surface p-8 text-center text-sm text-fg-muted">
            No cases in this worklist
          </div>
        ) : (
          <div className="panel-premium rounded border border-border/50 divide-y divide-border/50">
            {studies.map((study, idx) => {
              const primaryLabel =
                study.patient_id || study.accession
                  ? `${study.patient_id} · ${study.accession}`
                  : (study.study_description ?? study.study_instance_uid);
              const status = caseStatusById.get(study.case_id);
              return (
                <button
                  key={study.case_id}
                  onClick={() => handleOpen(idx)}
                  className="w-full flex items-center gap-4 px-5 py-4 hover:bg-white/2 transition text-left"
                >
                  <div className="w-8 text-fg-muted text-sm font-mono">{idx + 1}</div>
                  <FaFileAlt className="w-4 h-4 text-fg-muted shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-fg truncate">{primaryLabel}</div>
                    <div className="text-xs text-fg-muted truncate">{study.study_instance_uid}</div>
                  </div>
                  {status && (
                    <StatusBadge tone={status === "complete" ? "ghost" : "neutral"}>
                      {status === "complete" ? "Done" : "Pending"}
                    </StatusBadge>
                  )}
                  <FaChevronRight className="w-3 h-3 text-fg-muted" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default StudyView;
